import { isHttpMethod } from '@scalar/helpers/http/is-http-method';
/**
 * Normalizes the request object with defaults
 */
export function normalizeRequest(request) {
    return {
        ...request,
        method: normalizeMethod(request.method),
    };
}
/**
 * Encodes raw HAR query names and values when constructing a URL
 */
export function buildQueryString(queryParams) {
    if (!queryParams?.length) {
        return '';
    }
    const queryPairs = queryParams.map((param) => `${encodeURIComponent(param.name)}=${encodeURIComponent(param.value)}`);
    return `?${queryPairs.join('&')}`;
}
/**
 * Normalizes a request method.
 */
export const normalizeMethod = (method) => {
    if (!method) {
        return 'GET';
    }
    return isHttpMethod(method) && method === method.toLowerCase() ? method.toUpperCase() : method;
};
/**
 * Normalizes URL formatting while preserving origin-only paths.
 */
export const normalizeUrl = (url) => {
    if (!url) {
        return '';
    }
    try {
        const parsedUrl = new URL(url);
        if (parsedUrl.pathname === '/') {
            return `${parsedUrl.origin}${parsedUrl.search}${parsedUrl.hash}`;
        }
        return parsedUrl.toString();
    }
    catch {
        return url;
    }
};
/**
 * Joins URL and query string while preserving existing query values.
 */
export const joinUrlAndQuery = (url, queryString) => {
    const query = buildQueryString(queryString);
    if (!query) {
        return url;
    }
    if (!url) {
        return query;
    }
    const hashIndex = url.indexOf('#');
    const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
    return `${base}${base.includes('?') ? '&' : '?'}${query.slice(1)}${hash}`;
};
/**
 * Collects deduplicated headers and optional cookie headers.
 */
export const collectHeaders = (headers, cookies) => {
    const dedupedHeaders = new Map();
    headers?.forEach((header) => {
        if (header.name) {
            dedupedHeaders.set(header.name, header.value ?? '');
        }
    });
    if (cookies?.length) {
        const cookieHeader = cookies
            .map((cookie) => `${encodeURIComponent(cookie.name)}=${encodeURIComponent(cookie.value)}`)
            .join('; ');
        dedupedHeaders.set('Cookie', cookieHeader);
    }
    return Array.from(dedupedHeaders.entries()).map(([name, value]) => ({ name, value }));
};
/**
 * Adds a named value while preserving repeated keys as arrays.
 */
export const accumulateRepeatedValue = (data, name, value) => {
    const existingValue = data[name];
    if (existingValue === undefined) {
        data[name] = value;
    }
    else if (Array.isArray(existingValue)) {
        existingValue.push(value);
    }
    else {
        data[name] = [existingValue, value];
    }
};
/**
 * Reduces query parameters into an object while preserving repeated keys as arrays.
 */
export function reduceQueryParams(query = []) {
    return query.reduce((acc, { name, value }) => {
        accumulateRepeatedValue(acc, name, value);
        return acc;
    }, {});
}
/**
 * Processes headers and cookies into a headers object
 */
export function processHeaders(request) {
    const headers = {};
    // Process regular headers
    if (request.headers) {
        for (const header of request.headers) {
            if (header.value && !/[; ]/.test(header.name)) {
                headers[header.name] = header.value;
            }
        }
    }
    // Process cookies
    if (request.cookies && request.cookies.length > 0) {
        const cookieString = request.cookies
            .map((cookie) => `${encodeURIComponent(cookie.name)}=${encodeURIComponent(cookie.value)}`)
            .join('; ');
        headers['Cookie'] = cookieString;
    }
    return headers;
}
